import { HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  patchState,
  signalStoreFeature,
  withComputed,
  withHooks,
  withProps,
  withState,
} from '@ngrx/signals';
import { AppVersionNotificationComponent } from '@shared/components/app-verison-notification/app-version-notification.component';
import { isDefined } from '@stillmandigital/common';
import { catchError, filter, of, switchMap, timer } from 'rxjs';

const CHECK_VERSION_TIMEOUT = 5 * 60 * 1000; // 5 minutes
const CHECK_VERSION_URL = '/version.json';

export const useAppVersion = () =>
  signalStoreFeature(
    withState({
      currentVersion: undefined as string | undefined,
      previousVersion: undefined as string | undefined,
    }),
    withProps(() => ({
      _httpClient: inject(HttpClient),
      _snackBar: inject(MatSnackBar),
    })),
    withComputed(store => {
      const displayNewVersionNotification = computed(
        () =>
          isDefined(store.currentVersion()) &&
          isDefined(store.previousVersion()) &&
          store.currentVersion() !== store.previousVersion(),
      );

      return { displayNewVersionNotification };
    }),
    withHooks(store => ({
      onInit() {
        timer(0, CHECK_VERSION_TIMEOUT)
          .pipe(
            takeUntilDestroyed(),
            switchMap(() =>
              store._httpClient
                .get<{ version: string }>(CHECK_VERSION_URL, {
                  headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache, no-store, max-age=0',
                    Pragma: 'no-cache',
                    Expires: 'Thu, 23 Feb 2023 18:47:00 GMT',
                  }),
                })
                .pipe(catchError(() => of(null))),
            ),
          )
          .subscribe(response => {
            if (!response) {
              return;
            }
            patchState(store, state => ({
              currentVersion: response.version,
              previousVersion: isDefined(state.previousVersion)
                ? state.previousVersion
                : response.version,
            }));
          });

        toObservable(store.displayNewVersionNotification)
          .pipe(takeUntilDestroyed(), filter(Boolean))
          .subscribe(() => {
            store._snackBar.openFromComponent(AppVersionNotificationComponent, {
              panelClass: ['app-version-notification', 'warn'],
              duration: 0,
            });
          });
      },
    })),
  );
